import { useCallback, useEffect, useState } from 'react';
import { addMyKey, getMe, removeMyKey, type MyProfile } from '../api';
import { changePassword } from '../auth';
import type { PortalConfig } from '../config';
import { AddKeyForm, KeyList } from '../components/Keys';

// A partner's own account: how to connect, which keys work, and their password.
//
// Everything here operates on the username in their token. There is no field on
// this page that names a user, so there is nothing to tamper with -- the API
// ignores any username in the request body for these routes.
export function MyAccountPage({ config }: { config: PortalConfig }) {
  const [profile, setProfile] = useState<MyProfile>();
  const [error, setError] = useState<string>();

  const reload = useCallback(async () => {
    setError(undefined);
    try {
      setProfile(await getMe());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (error && !profile) return <p className="error">{error}</p>;
  if (!profile) return <p>Loading…</p>;

  const usesKey = profile.authMethod !== 'password';
  const usesPassword = profile.authMethod !== 'ssh-key';

  return (
    <section>
      <h1>My account</h1>
      {error && <p className="error">{error}</p>}

      <div className="card wide">
        <h2>Connecting over SFTP</h2>
        <dl>
          <dt>Host</dt>
          <dd>
            <code>{config.sftpEndpoint}</code>
          </dd>
          <dt>Port</dt>
          <dd>
            <code>22</code>
          </dd>
          <dt>Username</dt>
          <dd>
            <code>{profile.username}</code>
          </dd>
          <dt>Sign-in</dt>
          <dd>{DESCRIPTIONS[profile.authMethod]}</dd>
        </dl>
        <p className="hint">
          Example: <code>sftp {profile.username}@{config.sftpEndpoint}</code>
          {usesKey && (
            <>
              {' '}
              — add <code>-i ~/.ssh/your_key</code> to pick a specific key.
            </>
          )}
        </p>
        {profile.allowedSourceIps && profile.allowedSourceIps.length > 0 && (
          <p className="hint">
            Connections are only accepted from {profile.allowedSourceIps.join(', ')}. From anywhere
            else the connection is refused before your credentials are checked.
          </p>
        )}
      </div>

      <div className="card wide">
        <h2>My folders</h2>
        <table>
          <thead>
            <tr>
              <th>Folder</th>
              <th>Access</th>
              <th>What it is for</th>
            </tr>
          </thead>
          <tbody>
            {profile.folders.map((folder) => (
              <tr key={folder.path}>
                <td>
                  <code>{folder.path}</code>
                </td>
                <td>{ACCESS[folder.permission ?? 'read-write']}</td>
                <td className="hint">{folder.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {usesKey && (
        <div className="card wide">
          <h2>My SSH keys</h2>
          <p className="hint">
            Any of these keys can sign you in. To rotate one, add the replacement first and revoke
            the old one after you have tested it — you cannot remove your last key, because that
            would lock you out.
          </p>
          <KeyList
            keys={profile.publicKeys ?? []}
            onRemove={(keyId) => {
              void removeMyKey(keyId)
                .then(reload)
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : String(err))
                );
            }}
          />
          <AddKeyForm
            defaultComment={profile.username}
            onAdd={async (body) => {
              await addMyKey(body);
              await reload();
            }}
          />
        </div>
      )}

      {usesPassword && <ChangePassword />}
    </section>
  );
}

const DESCRIPTIONS = {
  'ssh-key': 'SSH key only. A password will not be accepted.',
  password: 'Password only.',
  'ssh-key-or-password': 'Either your SSH key or your password.',
} as const;

const ACCESS = {
  'read-write': 'Download and upload',
  'read-only': 'Download only',
  'write-only': 'Upload only — you can see what you sent but not download it back',
} as const;

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (next !== confirmation) {
      setError('The two new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirmation('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="card wide"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h2>Change my password</h2>
      <p className="hint">
        This is the same password you use for SFTP, so it changes there at the same time.
      </p>
      <label>
        Current password
        <input
          type="password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          autoComplete="current-password"
          required
        />
      </label>
      <label>
        New password
        <input
          type="password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
          required
        />
      </label>
      <label>
        New password again
        <input
          type="password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="new-password"
          required
        />
      </label>
      {error && <p className="error">{error}</p>}
      {done && <p className="ok">Password changed.</p>}
      <button type="submit" disabled={busy}>
        {busy ? 'Changing…' : 'Change password'}
      </button>
    </form>
  );
}
